"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Data ───────────────────────────────────────────────────────────────

type Company = {
  name: string;
  status: string;
  yieldLabel: string;
  price: number;
  dividend: number;
  eps: number;
  totalDividend: number;
  fcf: number;
  payoutRatio: number;
  tone: string;
};

const STRINGS: Record<
  Locale,
  {
    companyA: { name: string; status: string };
    companyB: { name: string; status: string };
    dividendYield: string;
    payoutRatio: string;
    overPaying: string;
    comfortable: string;
    sharePrice: (price: string) => string;
    dps: string;
    eps: string;
    totalDividend: string;
    fcf: string;
    yen: (value: string) => string;
    bnYen: (oku: number) => string;
    title: string;
    description: string;
    formula: string;
  }
> = {
  ja: {
    companyA: { name: "A社", status: "低めの利回り" },
    companyB: { name: "B社", status: "高利回り" },
    dividendYield: "配当利回り",
    payoutRatio: "配当性向",
    overPaying: "⚠ 利益を超えて配当を出している",
    comfortable: "利益に対して余裕のある水準",
    sharePrice: (price) => `株価 ${price}`,
    dps: "1株配当（DPS）",
    eps: "1株利益（EPS）",
    totalDividend: "配当総額",
    fcf: "FCF",
    yen: (value) => `${value}円`,
    bnYen: (oku) => `${oku.toLocaleString()}億円`,
    title: "利回りだけでは比べられない",
    description:
      "1株配当（DPS）、1株利益（EPS）、配当総額、FCFまで見ると、無理をしている配当かどうかが見えやすくなる",
    formula: "配当性向 = 1株配当（DPS） ÷ 1株利益（EPS）",
  },
  en: {
    companyA: { name: "Company A", status: "Lower yield" },
    companyB: { name: "Company B", status: "High yield" },
    dividendYield: "Dividend yield",
    payoutRatio: "Payout ratio",
    overPaying: "⚠ Paying out more than it earns",
    comfortable: "Comfortable relative to earnings",
    sharePrice: (price) => `Share price ${price}`,
    dps: "Dividend per share (DPS)",
    eps: "Earnings per share (EPS)",
    totalDividend: "Total dividends",
    fcf: "FCF",
    yen: (value) => `¥${value}`,
    bnYen: (oku) => `¥${(oku / 10).toFixed(1)}bn`,
    title: "Yield alone is not enough to compare",
    description:
      "Looking at DPS, EPS, total dividends, and FCF makes it much clearer whether a dividend is being stretched",
    formula: "Payout ratio = dividend per share (DPS) ÷ earnings per share (EPS)",
  },
};

const COMPANIES: Omit<Company, "name" | "status">[] = [
  {
    yieldLabel: "2%",
    price: 5000,
    dividend: 100,
    eps: 250,
    totalDividend: 80,
    fcf: 200,
    payoutRatio: 40,
    tone: COLOR.emerald,
  },
  {
    yieldLabel: "10%",
    price: 1000,
    dividend: 100,
    eps: 50,
    totalDividend: 40,
    fcf: 20,
    payoutRatio: 200,
    tone: COLOR.rose,
  },
];

const MAX_COMPANY_CASH = Math.max(...COMPANIES.map((company) => Math.max(company.totalDividend, company.fcf)));

// ── Layout ──────────────────────────────────────────────────────────────

const W = 420;
const PAD = 16;
const COL_GAP = 14;
const COL_W = (W - PAD * 2 - COL_GAP) / 2;

// Dots
const DOT_COUNT = 5;
const DOT_R = 3.8;
const DOT_GAP = 11;
const LABEL_W = 78;
const DOT_ZONE_W = DOT_COUNT * DOT_GAP;

// ── Helpers ─────────────────────────────────────────────────────────────

function formatYen(v: number, t: (typeof STRINGS)[Locale]) {
  return t.yen(v.toLocaleString());
}

function formatOkuYen(v: number, t: (typeof STRINGS)[Locale]) {
  return t.bnYen(v);
}

/** 値を5段階に変換（max を基準） */
function toLevel(value: number, max: number): number {
  const ratio = value / max;
  if (ratio >= 0.8) return 5;
  if (ratio >= 0.6) return 4;
  if (ratio >= 0.4) return 3;
  if (ratio >= 0.2) return 2;
  return 1;
}

// ── Sub-components ─────────────────────────────────────────────────────

function YieldBadge({ x, y, company }: { x: number; y: number; company: Company }) {
  const t = STRINGS[useDocLocale()];
  const bw = 58;
  const bh = 26;
  return (
    <g>
      <rect x={x} y={y} width={bw} height={bh} rx={6} fill={company.tone} opacity={0.12} />
      <text x={x + bw / 2} y={y + 11} textAnchor="middle" fontSize="6.5" fill={company.tone} opacity={0.8}>
        {t.dividendYield}
      </text>
      <text x={x + bw / 2} y={y + 22} textAnchor="middle" fontSize="11" fill={company.tone} fontWeight="800">
        {company.yieldLabel}
      </text>
    </g>
  );
}

function DotRow({
  x,
  y,
  label,
  value,
  filledCount,
  color,
}: {
  x: number;
  y: number;
  label: string;
  value: string;
  filledCount: number;
  color: string;
}) {
  const dotsX = x + LABEL_W;
  const valueX = dotsX + DOT_ZONE_W + 4;
  return (
    <g>
      <text x={x} y={y} fontSize="8" fill="currentColor" opacity={0.55} dominantBaseline="central">
        {label}
      </text>
      {Array.from({ length: DOT_COUNT }).map((_, i) => {
        const filled = i < filledCount;
        return (
          <circle
            key={i}
            cx={dotsX + i * DOT_GAP + DOT_R}
            cy={y}
            r={DOT_R}
            fill={filled ? color : "currentColor"}
            opacity={filled ? 0.5 : 0.08}
          />
        );
      })}
      <text
        x={valueX}
        y={y}
        fontSize="8.5"
        fill="currentColor"
        fontWeight="600"
        opacity={0.7}
        dominantBaseline="central"
      >
        {value}
      </text>
    </g>
  );
}

function PayoutRatioSection({ x, y, company }: { x: number; y: number; company: Company }) {
  const t = STRINGS[useDocLocale()];
  const isHigh = company.payoutRatio > 100;
  const color = isHigh ? COLOR.rose : company.tone;
  return (
    <g>
      <text x={x} y={y} fontSize="8" fill="currentColor" opacity={0.5}>
        {t.payoutRatio}
      </text>
      <text x={x + 38} y={y} fontSize="14" fill={color} fontWeight="800">
        {company.payoutRatio}%
      </text>
      <text x={x} y={y + 14} fontSize="7.5" fill={color} opacity={0.7}>
        {isHigh ? t.overPaying : t.comfortable}
      </text>
    </g>
  );
}

const ROW_GAP = 20;

function CompanyColumn({ x, company }: { x: number; company: Company }) {
  const t = STRINGS[useDocLocale()];
  const cx = x + 12;
  const perShareMaxVal = Math.max(company.dividend, company.eps);

  const rowStart = 60;
  const row2 = rowStart + ROW_GAP;
  const row3 = row2 + ROW_GAP;
  const row4 = row3 + ROW_GAP;
  const dividerY = row4 + 16;
  const payoutY = dividerY + 20;
  const cardH = payoutY + 30;

  return (
    <g>
      {/* Card background */}
      <rect
        x={x}
        y={0}
        width={COL_W}
        height={cardH}
        rx={10}
        fill={company.tone}
        opacity={0.05}
        stroke={company.tone}
        strokeWidth="1"
        strokeOpacity={0.2}
      />

      {/* Header */}
      <text x={cx} y={22} fontSize="13" fill="currentColor" fontWeight="700" opacity={0.85}>
        {company.name}
      </text>
      <text x={cx} y={35} fontSize="8" fill="currentColor" opacity={0.45}>
        {t.sharePrice(formatYen(company.price, t))}
      </text>
      <YieldBadge x={x + COL_W - 70} y={8} company={company} />

      {/* Divider */}
      <line x1={cx} y1={46} x2={x + COL_W - 12} y2={46} stroke="currentColor" strokeWidth="0.5" opacity={0.1} />

      {/* Metric dot rows — single line each */}
      <DotRow
        x={cx}
        y={rowStart}
        label={t.dps}
        value={formatYen(company.dividend, t)}
        filledCount={toLevel(company.dividend, perShareMaxVal)}
        color={company.tone}
      />
      <DotRow
        x={cx}
        y={row2}
        label={t.eps}
        value={formatYen(company.eps, t)}
        filledCount={toLevel(company.eps, perShareMaxVal)}
        color={company.tone}
      />
      <DotRow
        x={cx}
        y={row3}
        label={t.totalDividend}
        value={formatOkuYen(company.totalDividend, t)}
        filledCount={toLevel(company.totalDividend, MAX_COMPANY_CASH)}
        color={company.tone}
      />
      <DotRow
        x={cx}
        y={row4}
        label={t.fcf}
        value={formatOkuYen(company.fcf, t)}
        filledCount={toLevel(company.fcf, MAX_COMPANY_CASH)}
        color={company.tone}
      />

      {/* Divider */}
      <line
        x1={cx}
        y1={dividerY}
        x2={x + COL_W - 12}
        y2={dividerY}
        stroke="currentColor"
        strokeWidth="0.5"
        opacity={0.1}
      />

      {/* Payout ratio */}
      <PayoutRatioSection x={cx} y={payoutY} company={company} />
    </g>
  );
}

// ── Main ────────────────────────────────────────────────────────────────

const TOTAL_H = 200;

export function DocPayoutRatioDiagram() {
  const t = STRINGS[useDocLocale()];
  const companies: Company[] = [
    { ...COMPANIES[0]!, ...t.companyA },
    { ...COMPANIES[1]!, ...t.companyB },
  ];

  return (
    <IllustrationCard title={t.title} description={t.description}>
      <svg viewBox={`0 0 ${W} ${TOTAL_H}`} className="mx-auto w-full max-w-xl">
        {companies.map((company, i) => {
          const x = PAD + i * (COL_W + COL_GAP);
          return <CompanyColumn key={company.name} x={x} company={company} />;
        })}

        <text x={W / 2} y={TOTAL_H - 6} textAnchor="middle" fontSize="7.5" fill="currentColor" opacity={0.4}>
          {t.formula}
        </text>
      </svg>
    </IllustrationCard>
  );
}
